import React, { memo, useCallback, useState, useMemo, FC } from 'react';

// --- TYPE DEFINITIONS ---

type TextSize = 'small' | 'medium' | 'large';
type Language = 'en' | 'es';

interface AppState {
  textSize: TextSize;
  reduceMotion: boolean;
  language: Language;
}

interface HeroLogoProps {
  reduceMotion: boolean;
  onImageError?: () => void;
  onImageLoad?: () => void;
}

interface PhishingHeroProps {
  className?: string;
}

// --- MOCKED DEPENDENCIES (for a runnable example) ---

const useNavigate = () => (path: string) => {
  alert(`Navigating to: ${path}`);
};

const useAppStateContext = (): AppState => ({
  textSize: 'medium',
  reduceMotion: false,
  language: 'es',
});

const getSafeTextSizeClasses = (size: TextSize) => ({
    h1: 'text-4xl md:text-5xl',
    p: 'text-base md:text-lg',
    button: 'text-base',
});

const heroTranslations: Record<Language, any> = {
  es: {
    heading: 'Aprende a Pescar Estafadores',
    subheading: 'Protege a tus seres queridos del fraude en línea.',
    description: 'Nuestras simulaciones interactivas y guías fáciles de seguir enseñan a las personas mayores a identificar y evitar estafas de phishing de forma segura.',
    startButton: 'Comenzar Simulaciones',
    guidesButton: 'Ver Guías',
  },
  en: {
    heading: 'Learn to Spot Scammers',
    subheading: 'Protect your loved ones from online fraud.',
    description: 'Our interactive simulations and easy-to-follow guides teach seniors how to safely identify and avoid phishing scams.',
    startButton: 'Start Simulations',
    guidesButton: 'View Guides',
  },
};

// --- REFACTORED COMPONENTS ---

/**
 * Reusable logo component with accessibility features and error handling.
 */
const HeroLogo: FC<HeroLogoProps> = memo(({ reduceMotion, onImageError, onImageLoad }) => {
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = 'https://placehold.co/300x300/f97316/white?text=Logo';
    onImageError?.();
  };

  return (
    <div
      className={`relative max-w-[250px] h-auto flex items-center justify-center ${
        !reduceMotion ? 'transform transition-transform duration-500 ease-out hover:scale-105' : ''
      }`}
      role="img"
      aria-label="HelpGrandpaPhish application logo"
    >
      <img
        src="https://4tsix0yujj.ufs.sh/f/2vMRHqOYUHc0c60AM2XxLlIki8fCRNnvUyWdBhcZQKOVp6M0"
        alt="HelpGrandpaPhish Logo"
        className="max-w-full h-auto object-contain p-4"
        loading="eager"
        decoding="async"
        onError={handleImageError}
        onLoad={onImageLoad}
      />
      <div
        className={`absolute inset-0 bg-gradient-to-br from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 ${
          !reduceMotion ? 'transition-opacity duration-300' : ''
        }`}
        aria-hidden="true"
      />
    </div>
  );
});
HeroLogo.displayName = 'HeroLogo';

/**
 * PhishingHero Component: Main hero section for the home page.
 */
const PhishingHero: FC<PhishingHeroProps> = ({ className = '', ...props }) => {
  const { textSize, reduceMotion, language } = useAppStateContext();
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);

  const translations = useMemo(
    () => heroTranslations[language] || heroTranslations.es,
    [language]
  );

  const handleNavigation = useCallback((path: string) => {
    try {
      navigate(path);
    } catch (error) {
      console.error('Navigation error:', error);
      window.location.href = path;
    }
  }, [navigate]);

  return (
    <main
      className={`w-full py-20 px-4 sm:px-6 bg-gradient-to-br from-orange-50 via-gray-50 to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 ${className}`}
      role="main"
      aria-labelledby="hero-heading"
      {...props}
    >
      <div className="max-w-4xl mx-auto text-center bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-2xl shadow-xl p-8 sm:p-12">
        <div className="flex justify-center mb-4">
          <HeroLogo
            reduceMotion={reduceMotion}
            onImageError={() => console.warn('Hero logo failed to load.')}
            onImageLoad={() => setImageLoaded(true)}
          />
        </div>

        <h1
          id="hero-heading"
          className={`${getSafeTextSizeClasses(textSize).h1} font-extrabold text-gray-900 dark:text-gray-100 mb-4`}
        >
          {translations.heading}
        </h1>

        <p
          className={`${getSafeTextSizeClasses(textSize).p} text-orange-700 dark:text-orange-400 font-semibold mb-4`}
          role="doc-subtitle"
        >
          {translations.subheading}
        </p>

        <p
          className={`${getSafeTextSizeClasses(textSize).p} text-gray-600 dark:text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto`}
        >
          {translations.description}
        </p>

        <nav
          className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center"
          aria-label="Main navigation"
        >
          <button
            onClick={() => handleNavigation('/simulaciones')}
            className={`px-8 py-4 ${getSafeTextSizeClasses(textSize).button} font-bold rounded-xl bg-orange-600 text-white hover:bg-orange-700 focus:outline-none focus:ring-4 focus:ring-orange-300 dark:focus:ring-orange-800 transition-all duration-200 shadow-lg transform hover:scale-105`}
            type="button"
          >
            {translations.startButton}
          </button>
          <button
            onClick={() => handleNavigation('/guias')}
            className={`px-8 py-4 ${getSafeTextSizeClasses(textSize).button} font-bold rounded-xl bg-white text-orange-600 border-2 border-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-4 focus:ring-orange-300 dark:bg-gray-700 dark:text-orange-300 dark:border-orange-500 dark:hover:bg-gray-600 dark:focus:ring-orange-700 transition-all duration-200 shadow-lg transform hover:scale-105`}
            type="button"
          >
            {translations.guidesButton}
          </button>
        </nav>
      </div>
    </main>
  );
};

export default memo(PhishingHero);
